

<template>
  <nav class="pagination-container" aria-label="Pagination Navigation">
    <!-- Previous Button -->
    <button 
      class="pagination-btn" 
      :disabled="parsedCurrentPage === 1"
      @click="changePage(parsedCurrentPage - 1)"
      aria-label="Go to previous page"
    >
      <!-- &laquo;  -->
      Prev
    </button>

    <!-- Page Numbers & Dots -->
    <span v-for="(page, index) in visiblePages" :key="index" class="pagination-item-wrapper">
      <span v-if="page === '...'" class="pagination-dots">...</span>
      <button
        v-else
        class="pagination-btn page-num"
        :class="{ active: parsedCurrentPage === page }"
        @click="changePage(page)"
        :aria-current="parsedCurrentPage === page ? 'page' : undefined"
        :aria-label="'Go to page ' + page"
      >
        {{ page }}
      </button>
    </span>

    <!-- Next Button -->
    <button 
      class="pagination-btn" 
      :disabled="parsedCurrentPage === totalPages"
      @click="changePage(parsedCurrentPage + 1)"
    >
      Next
       <!-- &raquo; -->
    </button>
  </nav>
</template>

<script>
export default {
  name: 'PaginationNew',
  props: {
    currentPage: {
      type: [Number, String], // Accept strings from raw server data safely
      required: true,
      default: 1
    },
    totalItems: {
      type: [Number, String], // Accept strings from raw server data safely
      required: true
    },
    itemsPerPage: {
      type: [Number, String], // Accept strings from raw server data safely
      default: 10
    },
  },
  computed: {
    // 1. Force convert all values to actual JavaScript Numbers to fix type collisions
    parsedCurrentPage() {
      return Number(this.currentPage) || 1;
    },
    parsedTotalItems() {
      return Number(this.totalItems) || 0;
    },
    parsedItemsPerPage() {
      return Number(this.itemsPerPage) || 10;
    },

    // 2. Compute total page count using typed numbers
    totalPages() {
      if (this.parsedTotalItems <= 0) return 1;
      return Math.max(1, Math.ceil(this.parsedTotalItems / this.parsedItemsPerPage));
    },

    // 3. Dynamic layout window calculation
    visiblePages() {
      const total = this.totalPages;
      const current = this.parsedCurrentPage;
      
      const boundaryCount = 1; 
      const siblingCount = 1;  

      const maxButtons = boundaryCount * 2 + siblingCount * 2 + 3; 
      if (total <= maxButtons) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const leftSiblingIndex = Math.max(current - siblingCount, 1);
      const rightSiblingIndex = Math.min(current + siblingCount, total);

      const showLeftDots = leftSiblingIndex > boundaryCount + 2;
      const showRightDots = rightSiblingIndex < total - (boundaryCount + 1);

      const pages = [];
      pages.push(1);

      if (showLeftDots) {
        pages.push('...');
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
      } else {
        for (let i = 2; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
      }

      if (showRightDots) {
        pages.push('...');
        pages.push(total);
      } else {
        for (let i = rightSiblingIndex + 1; i <= total; i++) {
          pages.push(i);
        }
      }

      return pages;
    }
  },
  methods: {
    changePage(page) {
      const targetPage = Number(page);

      // Guard check handles mathematical ranges with clean matching numbers
      if (targetPage >= 1 && targetPage <= this.totalPages && targetPage !== this.parsedCurrentPage) {
        // console.log('Child Component is Emitting Destination:', targetPage);
        
        // Broadcasts both variants to catch any structural Vue 2 casing issues
        this.$emit('page-changed', targetPage);
        this.$emit('pagechanged', targetPage); 
      }
    }
  }
};
</script>

<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1.5rem 0;
  font-family: sans-serif;
}
.pagination-item-wrapper {
  display: inline-flex;
  gap: 0.5rem;
}
.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  background-color: #fff;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
  transition: all 0.2s ease;
  font-size: 12px;
}
.pagination-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
  border-color: #adadad;
}
.pagination-btn:disabled {
  background-color: #eaedd5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}
.page-num.active {
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
}
.pagination-dots {
  padding: 0.5rem;
  color: #666;
  user-select: none;
}
</style>



<!-- Vue 3 code starts -->
<!-- <template>
  <nav class="pagination-container" aria-label="Pagination Navigation">
   
    <button 
      class="pagination-btn" 
      :disabled="currentPage === 1"
      @click="changePage(currentPage - 1)"
      aria-label="Go to previous page"
    >
      &laquo; Prev
    </button>

   
    <template v-for="(page, index) in visiblePages" :key="index">
      <span v-if="page === '...'" class="pagination-dots">...</span>
      <button
        v-else
        class="pagination-btn page-num"
        :class="{ active: currentPage === page }"
        @click="changePage(page)"
        :aria-current="currentPage === page ? 'page' : undefined"
        :aria-label="`Go to page ${page}`"
      >
        {{ page }}
      </button>
    </template>


    <button 
      class="pagination-btn" 
      :disabled="currentPage === totalPages"
      @click="changePage(currentPage + 1)"
      aria-label="Go to next page"
    >
      Next &raquo;
    </button>
  </nav>
</template> -->

<!-- <script setup>
import { computed } from 'vue';

// 1. Define strict, typed component properties
const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  totalItems: {
    type: Number,
    required: true
  },
  itemsPerPage: {
    type: Number,
    default: 10
  }
});

// 2. Setup the custom event emitter
const emit = defineEmits(['page-changed']);

// 3. Compute total page count safely
const totalPages = computed(() => {
  return Math.max(1, Math.ceil(props.totalItems / props.itemsPerPage));
});

// 4. Calculate dynamic pagination window layout
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = props.currentPage;
  
  // Configuration: How many numbers to show surrounding the active page
  const boundaryCount = 1; // Pages to show at start and end (Page 1 and Page N)
  const siblingCount = 1;  // Pages to show directly left and right of current page

  // If total pages are small, show all numbers without truncation logic
  const maxButtons = boundaryCount * 2 + siblingCount * 2 + 3; 
  if (total <= maxButtons) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  // Calculate range indexes
  const leftSiblingIndex = Math.max(current - siblingCount, 1);
  const rightSiblingIndex = Math.min(current + siblingCount, total);

  // Check if dots are required on either side
  const showLeftDots = leftSiblingIndex > boundaryCount + 2;
  const showRightDots = rightSiblingIndex < total - (boundaryCount + 1);

  const pages = [];

  // Always render first page
  pages.push(1);

  // Handle left side layout
  if (showLeftDots) {
    pages.push('...');
    // Add siblings
    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
      pages.push(i);
    }
  } else {
    // Fill up the space between 1 and the right sibling
    for (let i = 2; i <= rightSiblingIndex; i++) {
      pages.push(i);
    }
  }

  // Handle right side layout
  if (showRightDots) {
    pages.push('...');
    pages.push(total);
  } else {
    // Fill remaining pages up to total if no dots needed on right
    for (let i = rightSiblingIndex + 1; i <= total; i++) {
      pages.push(i);
    }
  }

  return pages;
});

// 5. Emit page changes to the parent controller
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('page-changed', page);
  }
};
</script> -->

<!-- <style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1.5rem 0;
  font-family: sans-serif;
}

.pagination-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  background-color: #fff;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f0f0f0;
  border-color: #adadad;
}

.pagination-btn:disabled {
  background-color: #eaedd5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.page-num.active {
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
}

.pagination-dots {
  padding: 0.5rem;
  color: #666;
  user-select: none;
}
</style> -->
<!-- vue 3 code ends -->




<!-- <template>
    <div>
      <div class="pagination">
        <button @click="change(page-1)" :disabled="page===1">Prev</button>
    
        <button
          v-for="p in pages"
          :key="p"
          :class="{ active: p===page }"
          @click="change(p)">
          {{ p }}
        </button>
    
        <button @click="change(page+1)" :disabled="page===totalPages">Next</button>
      </div>
    </div>
</template>
  
  <script setup>
  import { computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
import { directive } from 'vue/types/umd'
  
  const props = defineProps({ totalPages: Number })
  
  const route = useRoute()
  const router = useRouter()
  
  const page = computed(() => Number(route.query.page) || 1)
  
  const pages = computed(() => {
    const arr = []
    for (let i = 1; i <= props.totalPages; i++) arr.push(i)
    return arr
  })
  
  const change = (p) => {
    router.push({ query: { ...route.query, page: p } })
  }
  </script> -->